import { STATUS } from '@front-app/constants/state';
import {
	createOrganization,
	getAllOrganizations,
	getOrganization,
} from '@front-app/services/OrganizationsApi/OrganizationApi';
import { CreateOrganizationRequest, FullOrganization, PartialOrganization } from '@front-app/services/OrganizationsApi/OrganizationModel';
import { computed, ref, Ref } from '@vue/composition-api';
import { STARTABLE_STATUSES } from '../util/cache';

export interface OrganizationStore {
	get: (id: number) => Ref<FullOrganization>;
	fetch: (id: number, force?: boolean) => void;
	isLoading: (id: number) => boolean;
	fetchAllPartial: (force?: boolean) => Promise<void>;
	create: (payload: CreateOrganizationRequest) => Promise<FullOrganization>;
	allPartial: Ref<PartialOrganization[]>;
	isLoadingAllPartial: Ref<boolean>;
}

export const getOrganizationStore = (): OrganizationStore => {
	const allPartial: Ref<PartialOrganization[]> = ref([]);
	const organizationDetailsStatus: Record<number, Ref<STATUS>> = {};
	const organizationDetails: Record<number, Ref<FullOrganization>> = {};
	const getStatus = (id: number): Ref<STATUS> => {
		if (!organizationDetailsStatus[id]) {
			organizationDetailsStatus[id] = ref(STATUS.UNSTARTED);
		}
		return organizationDetailsStatus[id];
	};
	const get = (id: number): Ref<FullOrganization> => {
		if (!organizationDetails[id]) {
			organizationDetails[id] = ref(getEmptyOrganization(id));
		}
		return organizationDetails[id];
	};
	const fetch = async (id: number, force = false) => {
		const org = get(id);
		const status = getStatus(id);
		if (force || STARTABLE_STATUSES.includes(status.value)) {
			status.value = STATUS.LOADING;
			try {
				org.value = await getOrganization(id);
				status.value = STATUS.SUCCESS;
			} catch (e) {
				status.value = STATUS.ERROR;
			}
		}
	};
	const isLoading = (id: number): boolean => getStatus(id).value === STATUS.LOADING
		|| getStatus(id).value === STATUS.UNSTARTED;

	const fetchAllPartialStatus = ref(STATUS.UNSTARTED);
	const fetchAllPartial = async (force = false) => {
		if (force || STARTABLE_STATUSES.includes(fetchAllPartialStatus.value)) {
			fetchAllPartialStatus.value = STATUS.LOADING;
			try {
				allPartial.value = await getAllOrganizations();
				fetchAllPartialStatus.value = STATUS.SUCCESS;
			} catch (e) {
				fetchAllPartialStatus.value = STATUS.ERROR;
			}
		}
	};
	const isLoadingAllPartial = computed(() => fetchAllPartialStatus.value === STATUS.LOADING
		|| fetchAllPartialStatus.value === STATUS.UNSTARTED);

	const create = async (payload: CreateOrganizationRequest) => {
		const result = await createOrganization(payload);
		allPartial.value = Array.prototype.concat(allPartial.value, [toPartial(result)]);
		organizationDetailsStatus[result.id] = ref(STATUS.SUCCESS);
		organizationDetails[result.id] = ref(result);
		return result;
	};

	return {
		get,
		fetch,
		isLoading,
		allPartial,
		fetchAllPartial,
		isLoadingAllPartial,
		create,
	};
};

function toPartial(input: FullOrganization): PartialOrganization {
	return {
		id: input.id,
		type: input.type,
		name: input.name,
		logoSrc: input.logoSrc,
	};
}

function getEmptyOrganization(id: number): FullOrganization {
	return {
		id,
		type: undefined,
		name: '',
		logoSrc: '',
		address: {
			street1: '',
			street2: '',
			city: '',
			provStateRegion: null,
			country: null,
			postalCode: '',
			contactInfo: {
				phone: '',
				extension: '',
				mobile: '',
				fax: '',
				webaddress: '',
			},
		},
	};
}
